import React, { useState, useEffect } from "react";
import { ParticipantAdminEditDisplay } from "../../Interfaces/FundraiserRole";
import ImagePreview from "./ImageProfilePreview";
import { PrimaryDivision, SecondaryDivision } from "../../Interfaces/Division";

interface SingleParticipantRowProps {
  participant: ParticipantAdminEditDisplay;
  possibleDivisions: PrimaryDivision[];
  updateParticipantChanges: (
    participant_id: number,
    updatedData: Record<string, any>
  ) => void;
}

// Set up for profile picture removal in the future
const onImageRemove = (removalReason: string, participant_id: number) => {
  return null;
};

const SingleParticipantRow: React.FC<SingleParticipantRowProps> = ({
  participant,
  possibleDivisions,
  updateParticipantChanges,
}) => {
  const handlePrimaryDivisionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedPrimaryId = Number(e.target.value);
    setPrimaryDivision(selectedPrimaryId);

    const selectedPrimary = possibleDivisions.find(
      (division) => division.id === selectedPrimaryId
    );
    // Set secondary to -1 as if major and minor both have white sox
    // need to ensure the minor white sox is selected (if changing from major to minor)
    if (selectedPrimary) {
      setCurrentSecondaryDivisions(selectedPrimary.secondary_divisions);
      setSecondaryDivision("");
      updateParticipantChanges(participant.participant_id, {
        primary_division_id: selectedPrimaryId,
        secondary_division_id: -1,
      });
    } else {
      setCurrentSecondaryDivisions([]);
    }
  };

  const handleSecondaryDivisionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedSecondaryId = Number(e.target.value);
    updateParticipantChanges(participant.participant_id, {
      secondary_division_id: selectedSecondaryId,
    });
    setSecondaryDivision(selectedSecondaryId);
  };
  // get the default primary and secondary divisions
  const [primaryDivision, setPrimaryDivision] = useState<number | "">(
    participant.primary_division?.id || ""
  );
  const [secondaryDivision, setSecondaryDivision] = useState<number | "">(
    participant.secondary_division?.id || ""
  );
  const [currentSecondaryDivisions, setCurrentSecondaryDivisions] = useState<
    SecondaryDivision[]
  >(() => {
    const primaryDiv = possibleDivisions.find(
      (division) =>
        Number(division.id) === Number(participant.primary_division?.id)
    );
    return primaryDiv?.secondary_divisions || [];
  });

  // upon load up ensure current secondary division array has proper divisions
  useEffect(() => {
    if (possibleDivisions.length > 0 && primaryDivision) {
      // Find the primary division using the correct type matching
      const primaryDiv = possibleDivisions.find(
        (division) => Number(division.id) === Number(primaryDivision)
      );

      setCurrentSecondaryDivisions(primaryDiv?.secondary_divisions || []);
    }
  }, [primaryDivision, possibleDivisions]);

  return (
    <tr>
      <td>
        <ImagePreview participant={participant} onRemove={onImageRemove} />
      </td>
      <td>{participant.full_name}</td>
      <td>
        <div className="participant-join-form-form-group">
          <select
            id="primaryDivision"
            value={primaryDivision || ""}
            onChange={handlePrimaryDivisionChange}
            className="participant-join-form-select"
          >
            <option value="" disabled>
              Select Primary Division
            </option>
            {possibleDivisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.division_name}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td>
        <div className="participant-join-form-form-group">
          <select
            id="secondaryDivision"
            value={secondaryDivision || ""}
            onChange={handleSecondaryDivisionChange}
            className="participant-join-form-select"
            disabled={!primaryDivision}
          >
            <option value="" disabled>
              Select Secondary Division
            </option>
            {currentSecondaryDivisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.division_name}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td>{participant.has_boughtout ? "Yes" : "No"}</td>
      <td>${participant.profit_raised}</td>
      <td>{participant.contacts_inputted}</td>
    </tr>
  );
};

export default SingleParticipantRow;
